import { useTranslation } from 'react-i18next';
import { Input } from "@shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Slider } from "@shared/components/ui/slider";
import { Button } from "@shared/components/ui/button";
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { useDistricts, usePropertyTypes } from '../api/properties.queries';
import type { UIFilters, PropertyFiltersProps } from '../types';

export default function PropertyFilters({ filters, setFilters, onReset }: PropertyFiltersProps) {
  const { t } = useTranslation();
  const { data: districts, isLoading: districtsLoading } = useDistricts();
  const { data: propertyTypes, isLoading: typesLoading } = usePropertyTypes();

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-slate-900">{t('filters.title')}</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={onReset} className="text-slate-500 hover:text-slate-700">
          <X className="w-4 h-4 ml-1" />
          {t('filters.reset')}
        </Button>
      </div>

      <div className="space-y-5">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder={t('filters.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pr-10 h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">{t('filters.district')}</label>
          <Select
            value={filters.district}
            onValueChange={(value) => setFilters({ ...filters, district: value })}
            disabled={districtsLoading}
          >
            <SelectTrigger className="h-12 border-slate-200">
              {districtsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('common.loading')}</span>
                </div>
              ) : (
                <SelectValue placeholder={t('filters.allDistricts')} />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allDistricts')}</SelectItem>
              {districts?.map((district) => (
                <SelectItem key={district.code} value={district.code}>
                  {district.label}
                  {district.listingCount !== undefined && (
                    <span className="text-slate-400 mr-2">({district.listingCount})</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">{t('filters.propertyType')}</label>
          <Select
            value={filters.propertyType}
            onValueChange={(value) => setFilters({ ...filters, propertyType: value })}
            disabled={typesLoading}
          >
            <SelectTrigger className="h-12 border-slate-200">
              {typesLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('common.loading')}</span>
                </div>
              ) : (
                <SelectValue placeholder={t('filters.allTypes')} />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
              {propertyTypes?.map((type) => (
                <SelectItem key={type.code} value={type.code}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">{t('filters.bedrooms')}</label>
          <Select
            value={filters.bedrooms}
            onValueChange={(value) => setFilters({ ...filters, bedrooms: value })}
          >
            <SelectTrigger className="h-12 border-slate-200">
              <SelectValue placeholder={t('filters.anyBedrooms')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.anyBedrooms')}</SelectItem>
              <SelectItem value="2">2 {t('filters.rooms')}</SelectItem>
              <SelectItem value="3">3 {t('filters.rooms')}</SelectItem>
              <SelectItem value="4">4 {t('filters.rooms')}</SelectItem>
              <SelectItem value="5">5+ {t('filters.rooms')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-slate-700">{t('filters.priceRange')}</label>
            <span className="text-sm text-emerald-600 font-medium">
              {formatPrice(filters.priceRange[0])} - {formatPrice(filters.priceRange[1])} {t('properties.sar')}
            </span>
          </div>
          <Slider
            value={filters.priceRange}
            onValueChange={(value) => setFilters({ ...filters, priceRange: value as [number, number] })}
            min={500000}
            max={10000000}
            step={100000}
            className="[&_[role=slider]]:bg-emerald-600"
          />
        </div>
      </div>
    </div>
  );
}
